import React, { Component } from 'react';
import { ColorResult } from 'react-color';
import { errorAlert, confirmPopupBefore, confirmSuccessAfter, popupErrorFromServer } from '../util/helperFunctions';
import { Container } from 'bloomer/lib/layout/Container';
import Conditional from '../components/Conditional';
import { Button } from 'bloomer/lib/elements/Button';
import api from '../util/api';
import { RouteComponentProps } from '@reach/router';
import { Box } from 'bloomer/lib/elements/Box';
import { Icon } from 'bloomer/lib/elements/Icon';
import { Level } from 'bloomer/lib/components/Level/Level';
import { LevelRight } from 'bloomer/lib/components/Level/LevelRight';
import { LevelItem } from 'bloomer/lib/components/Level/LevelItem';
import MarginlessTitle from '../elements/MarginlessTitle';
import Spacer from '../elements/Spacer';
import AddNewSpecialNeedForm, { SpecialNeedsForm } from '../components/AddNewSpecialNeedForm';
import ListSpecialNeeds from '../components/ListSpecialNeeds';

interface PropTypes extends RouteComponentProps<{ animalType: animalType }> {}

interface StateTypes {
  specialNeeds: SpecialNeed[];
  form: SpecialNeedsForm;
  isAddingNew: boolean;
}

const formDefault: StateTypes['form'] = {
  name: '',
  acronym: '',
  color: {
    hsl: { h: 0, s: 0.9480519480519479, l: 0.44748148148148154, a: 1 },
    hex: '#de0606',
    rgb: { r: 222, g: 6, b: 6, a: 1 },
  },
  underline: false,
  sort: 'none',
  priority: '10',
  playgroup: 'false',
};

class ManageSpecialNeeds extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    specialNeeds: [],
    form: Object.assign({}, formDefault),
    isAddingNew: false,
  };

  parse = (val: string | ColorResult) => {
    if (typeof val !== 'string') {
      return val;
    }

    const numberRegExp = /^\d+$/;
    if (numberRegExp.test(val)) {
      return parseInt(val, 10);
    }

    if (val === 'true') {
      return true;
    }

    if (val === 'false') {
      return false;
    }

    return val;
  };

  updateForm = (key: keyof StateTypes['form'], value: string | ColorResult) => {
    const form: SpecialNeedsForm = Object.assign({}, this.state.form);
    const parsedValue = this.parse(value) as StateTypes['form'][keyof StateTypes['form']];
    form[key] = parsedValue;
    this.setState({ form });
  };

  handleColorChange = (color: ColorResult) => this.updateForm('color', color);

  handleChange = (e: React.FormEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement;
    return this.updateForm(target.name as keyof StateTypes['form'], target.value);
  };

  refresh = async (props = this.props) => {
    try {
      const result = await api.specialNeeds.get({
        animalType: props.animalType,
      });

      this.setState({
        specialNeeds: result,
      });
    } catch (err) {
      errorAlert(err);
    }
  };

  handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const { form } = this.state;
      const { animalType } = this.props;

      const specialBehaviours: SpecialNeed['specialBehaviours'] = [];
      if (form.underline) {
        specialBehaviours.push('underline');
      }

      if (form.sort !== 'none') {
        const sortKey = `sort-${form.sort}` as
          | 'sort-bottom-list'
          | 'sort-top-list'
          | 'sort-bottom-room'
          | 'sort-top-room';
        specialBehaviours.push(sortKey);
      }

      if (!form.playgroup) {
        specialBehaviours.push(`no-playgroup`);
      }

      const specialNeed: Partial<SpecialNeed> = {
        animalType,
        specialBehaviours,
        name: form.name,
        acronym: form.acronym,
        color: form.color.hex,
        priority: parseInt(form.priority, 10),
      };

      await confirmSuccessAfter('Special need created successfully', () => api.specialNeeds.post(specialNeed));

      this.refresh();
      this.resetForm();
    } catch (err) {
      popupErrorFromServer(err);
    }
  };

  resetForm = () => {
    this.setState({
      form: formDefault,
    });
  };

  createHandleDelete = (need: SpecialNeed) => async () => {
    const { _id, name } = need;
    try {
      confirmPopupBefore(`Are you sure you want to delete ${name}`, () =>
        confirmSuccessAfter(`${name} deleted successfully!`, async () => {
          const res = await api.specialNeeds.delete(_id);
          this.refresh();
          return res;
        }),
      );
    } catch (err) {
      popupErrorFromServer(err);
    }
  };

  componentWillReceiveProps(newProps: PropTypes) {
    if (this.props !== newProps) {
      this.refresh(newProps);
    }
  }

  componentDidMount() {
    this.refresh();
  }

  handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    this.resetForm();
  };

  toggleNew = () => {
    this.setState({
      isAddingNew: !this.state.isAddingNew,
    });
  };

  render() {
    const { specialNeeds, form, isAddingNew } = this.state;
    const { animalType } = this.props;
    // tslint:disable:jsx-no-lambda
    return (
      <Container>
        <Box>
          <Level>
            <MarginlessTitle isSize={1}>Manage Special Needs</MarginlessTitle>
            <LevelRight>
              <LevelItem>
                <Conditional check={!isAddingNew}>
                  <Button isColor="primary" onClick={this.toggleNew}>
                    <Icon isSize="medium" className="fa fa-plus" /> <Spacer>Add new {animalType} special need</Spacer>
                  </Button>
                </Conditional>
              </LevelItem>
              <LevelItem>
                <Conditional check={isAddingNew}>
                  <Button isColor="primary" onClick={this.toggleNew}>
                    <Icon isSize="medium" className="fa fa-plus" /> <Spacer>View all {animalType} special needs</Spacer>
                  </Button>
                </Conditional>
              </LevelItem>
            </LevelRight>
          </Level>
          <Conditional check={!isAddingNew}>
            <ListSpecialNeeds specialNeeds={specialNeeds} createHandleDelete={this.createHandleDelete} />
          </Conditional>
          <Conditional check={isAddingNew}>
            <AddNewSpecialNeedForm
              handleClear={this.handleClear}
              handleChange={this.handleChange}
              handleColorChange={this.handleColorChange}
              handleSubmit={this.handleSubmit}
              form={form}
            />
          </Conditional>
        </Box>
      </Container>
    );
  }
}

export default ManageSpecialNeeds;
